import React, { useState, useMemo } from "react";
import { X, Users, Search } from "lucide-react";

const ViewAllParticipantsModal = ({ isOpen, onClose, attendees }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");

	const getPendingStatuses = ["pending", "tentative", "needsAction"];

	const getNormalizedStatus = (status) => {
		if (!status || getPendingStatuses.includes(status)) return "pending";
		return status;
	};

	const filteredAttendees = useMemo(() => {
		return attendees.filter((attendee) => {
			const matchesSearch = attendee.email
				.toLowerCase()
				.includes(searchQuery.toLowerCase());
			if (statusFilter === "all") return matchesSearch;

			const normalizedStatus = getNormalizedStatus(attendee.responseStatus);
			return matchesSearch && normalizedStatus === statusFilter;
		});
	}, [attendees, searchQuery, statusFilter]);

	const statusCounts = useMemo(() => {
		return attendees.reduce((acc, attendee) => {
			const status = getNormalizedStatus(attendee.responseStatus);
			acc[status] = (acc[status] || 0) + 1;
			acc.all = (acc.all || 0) + 1;
			return acc;
		}, {});
	}, [attendees]);

	if (!isOpen) return null;

	const getInitials = (email) => {
		return email.split("@")[0].charAt(0).toUpperCase();
	};

	const StatusFilterButton = ({ status, label }) => (
		<button
			onClick={() => setStatusFilter(status)}
			className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
				statusFilter === status
					? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
					: "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
			}`}
		>
			{label} {statusCounts[status] ? `(${statusCounts[status]})` : ""}
		</button>
	);

	return (
		<div className="fixed inset-0 z-[1020] flex items-center justify-center p-4">
			<div className="fixed inset-0 bg-black/50" onClick={onClose} />
			<div className="relative bg-white dark:bg-gray-900 rounded-lg w-full max-w-lg">
				<div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600/20">
					<div className="flex items-center gap-2">
						<Users className="w-5 h-5 text-gray-600 dark:text-gray-300" />
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							All Participants ({attendees.length})
						</h3>
					</div>
					<button
						onClick={onClose}
						className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="p-4 border-b border-gray-200 dark:border-gray-600/20 space-y-4">
					{/* Search Input */}
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
						<input
							type="text"
							placeholder="Search participants..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full pl-9 pr-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
						/>
					</div>

					{/* Status Filters */}
					<div className="flex flex-wrap gap-2">
						<StatusFilterButton status="all" label="All" />
						<StatusFilterButton status="accepted" label="Accepted" />
						<StatusFilterButton status="pending" label="Pending" />
						<StatusFilterButton status="declined" label="Declined" />
					</div>
				</div>

				<div className="p-4 h-[40vh] overflow-y-auto scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar scrollbar-thumb-slate-300 scrollbar-track-slate-100 dark:scrollbar-thumb-slate-800 dark:scrollbar-track-slate-900">
					{filteredAttendees.length === 0 ? (
						<div className="text-center py-8 text-gray-500 dark:text-gray-400">
							No participants match your search criteria
						</div>
					) : (
						<div className="space-y-3">
							{filteredAttendees.map((attendee, index) => (
								<div
									key={index}
									className="flex items-center justify-between py-2"
								>
									<div className="flex items-center gap-2 flex-1 min-w-0">
										<div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
											<span className="text-sm text-gray-600 dark:text-gray-300">
												{getInitials(attendee.email)}
											</span>
										</div>
										<span className="text-sm text-gray-600 dark:text-gray-300 truncate">
											{attendee.email}
										</span>
									</div>
									<span
										className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
											attendee.responseStatus === "accepted"
												? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
												: attendee.responseStatus === "declined"
												? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
												: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400"
										}`}
									>
										{attendee.responseStatus === "tentative"
											? "pending (tentative)"
											: attendee.responseStatus === "needsAction"
											? "pending (needs action)"
											: attendee.responseStatus || "pending"}
									</span>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ViewAllParticipantsModal;
